import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../../config/config';

export type LeaveStatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveAttachment {
  attachmentUrl: string;
}

export interface LeaveRequestItem {
  leaveRequestId: number;
  leaveTypeName: string;
  statusSlug: string;
  displayStatus: string;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  reason: string;
  actionedByName: string | null;
  actionedByImage: string | null;
  remarks: string | null;
  cancelledDate: string | null;
  cancelledTime: string | null;
  attachments: LeaveAttachment[];
}

export interface LeaveStatusCounts {
  pending: number;
  approved: number;
  rejected: number;
  cancelled: number;
}

export interface LeaveListBody {
  leaveRequests: LeaveRequestItem[];
  statusCounts: LeaveStatusCounts;
  totalCount: number;
  maxPageNumber: number;
  maxLeaveDaysAllowed: number;
}

export interface LeaveType {
  leaveTypeId: number;
  leaveTypeName: string;
  isStartTimeRequired: boolean;
  isEndTimeRequired: boolean;
}

// Leave Request — parent mobile app's own endpoints (LeaveRequestList.js /
// ApplyLeaveForm.js / LeaveRequestDetail.js). studentId/classId are
// resolved server-side from the auth token, so unlike diary/attendance no
// student/class params are sent here at all — see the API reference this
// was built from.
@Injectable({ providedIn: 'root' })
export class ParentLeaveRequestService {

  private base = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) { }

  private authHeaders(): HttpHeaders | undefined {
    const raw = localStorage.getItem('authToken');
    if (!raw) { return undefined; }
    try {
      return new HttpHeaders().append('authToken', JSON.parse(raw));
    } catch {
      return new HttpHeaders().append('authToken', raw);
    }
  }

  getLeaveRequestList(status: LeaveStatusFilter, page: number, limit: number) {
    return this.http.get(`${this.base}/get/student/leave/request/list/for/parent`, {
      params: { status, startDate: '', endDate: '', page: String(page), limit: String(limit) },
      headers: this.authHeaders(),
    });
  }

  getLeaveTypes(lang: string) {
    return this.http.get(`${this.base}/get/student/leave/types`, {
      params: { lang },
      headers: this.authHeaders(),
    });
  }

  submitLeaveRequest(formData: FormData) {
    return this.http.post(`${this.base}/submit/student/leave/request`, formData, {
      headers: this.authHeaders(),
    });
  }

  cancelLeaveRequest(leaveRequestId: number, reason: string) {
    return this.http.put(`${this.base}/cancel/student/leave/request`, { leaveRequestId, reason }, {
      headers: this.authHeaders(),
    });
  }
}
